import React, { useEffect, useRef, useCallback } from "react";
import { Box, HStack, Stack, Text } from "@chakra-ui/react";
import { Required } from "./Required";

import { CreatableSelect } from "chakra-react-select";
import { searchForInstruments } from "../../../api";
import { useDebounce } from "../../../features/hooks";
import { StockLogo } from "../../design_library";
import { InstrumentType } from "../../../types/Enums";
import { track, EVENTS } from "../../../utils/analytics";

interface InstrumentOption {
  value: string;
  label: string;
  data?: any;
  __isNew__?: boolean;
}

export const InstrumentSearch = (props: {
  label: string;
  instrumentType: InstrumentType;
  value: string;
  field: string;
  error: string;
  enableCreateNew?: boolean;
  onChange: (field: string, value: string) => void;
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [options, setOptions] = React.useState<Array<InstrumentOption>>([]);
  const initialised = useRef<boolean>(false);

  useEffect(() => {
    if (initialised.current === false) {
      initialised.current = true;
      setIsLoading(false);
    }
  });

  const getSearchResults = async (query: string) => {
    if (query.length === 0) {
      setOptions([]);
      return;
    }
    setIsLoading(true);
    const results = await searchForInstruments(
      query.toUpperCase(),
      props.instrumentType
    );

    if (results?.forSearch) {
      setOptions(results.forSearch);
      setIsLoading(false);

      // Track successful search results
      if (results.forSearch.length > 0) {
        track(EVENTS.SEARCH.SEARCH_RESULTS_VIEWED, {
          query: query,
          result_count: results.forSearch.length,
          instrument_type: props.instrumentType,
          context: props.label,
        });
      } else {
        track(EVENTS.SEARCH.NO_RESULTS_FOUND, {
          query: query,
          instrument_type: props.instrumentType,
          context: props.label,
        });
      }
    }
  };

  const primeSearch = useDebounce(getSearchResults, 400);

  // Handle instrument selection with tracking
  const handleChange = useCallback(
    (selection: InstrumentOption) => {
      props.onChange(props.field, selection);

      // Track instrument selection
      if (selection) {
        const isNewInstrument = selection.__isNew__ === true;

        track(EVENTS.SEARCH.SEARCH_RESULT_CLICKED, {
          instrument: selection.value,
          instrument_name: selection.label,
          instrument_type: props.instrumentType,
          is_new_instrument: isNewInstrument,
          context: props.label,
        });
      }
    },
    [props]
  );

  return (
    <Stack spacing="4" mt="2" mb="1">
      <Text>
        {props.label}
        <Required />
      </Text>
      {props.enableCreateNew === true && (
        <CreatableSelect
          components={{ Option }}
          isLoading={isLoading}
          tagVariant="solid"
          options={
            options &&
            options.map((item: InstrumentOption) => {
              return { value: item.value, label: item.label, data: item };
            })
          }
          noOptionsMessage={() => "No results found"}
          formatCreateLabel={(inputValue) =>
            `Press return to add: ${inputValue} (New)`
          }
          allowCreateWhileLoading={false}
          onInputChange={(e) => {
            primeSearch(e);
          }}
          onChange={handleChange}
        />
      )}

      <Text color="red.500">{props.error}</Text>
    </Stack>
  );
};

const Option = (props: any) => {
  const { data } = props;

  if (data.__isNew__) return <Text pl="4"> {data.label}</Text>;
  return (
    <Box
      p="2"
      {...props.innerProps}
      sx={{
        "&:hover": {
          bg: "var(--bg)",
        },
      }}
    >
      <HStack>
        <StockLogo stock={data.value} size="30" />
        <Text>({data.value})</Text>
        <Text>{data.label}</Text>
      </HStack>
    </Box>
  );
};
